import React, { useCallback, useMemo, useState } from 'react'
import { ToggleButtonGroup, ToggleButton, Button, Box } from '@mui/material'
import ArrowDropDown from '@mui/icons-material/ArrowDropDown'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { useTimeRange } from './TimeRangeContext'

type Preset = 'today' | 'day' | 'week' | 'month' | 'year' | 'custom'

const inferPreset = (startDate: string, endDate: string): Preset => {
  const now = dayjs()
  const start = dayjs(startDate)
  const end = dayjs(endDate)
  const diffDays = end.diff(start, 'day')
  if (start.isSame(now, 'day') && end.isSame(now, 'day')) return 'today'
  if (diffDays === 1) return 'day'
  if (diffDays === 7) return 'week'
  if (diffDays >= 28 && diffDays <= 31) return 'month'
  if (diffDays >= 364 && diffDays <= 366) return 'year'
  return 'custom'
}

const fmt = (d: string) => dayjs(d).format('D MMM YYYY')

const TimeRangeFilter: React.FC = () => {
  const { setPreset, setRange, startDate, endDate } = useTimeRange()
  const [open, setOpen] = useState(false)
  const [customStart, setCustomStart] = useState<Dayjs | null>(dayjs(startDate))
  const [customEnd, setCustomEnd] = useState<Dayjs | null>(dayjs(endDate))

  const rangeParam = useMemo(() => inferPreset(startDate, endDate), [startDate, endDate])

  const handleChange = useCallback(
    (event: React.MouseEvent<HTMLElement>, newOption: Preset | null) => {
      if (!newOption) return
      if (newOption === 'custom') {
        setOpen((v) => !v)
        return
      }
      setOpen(false)
      setPreset(newOption)
    },
    [setPreset]
  )

  const handleApply = useCallback(() => {
    if (!customStart || !customEnd) return
    if (customEnd.isBefore(customStart)) return
    setRange({
      startDate: customStart.format('YYYY-MM-DD'),
      endDate: customEnd.format('YYYY-MM-DD')
    })
  }, [customStart, customEnd, setRange])

  const invalid = !customStart || !customEnd || customEnd.isBefore(customStart)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
      <Box display='flex' alignItems='center' gap={1.5} flexWrap='wrap'>
        <ToggleButtonGroup
          exclusive
          value={rangeParam}
          onChange={handleChange}
          aria-label='time range'
          sx={{ bgcolor: 'white' }}
        >
          <ToggleButton value='today' aria-label='Today'>
            Today
          </ToggleButton>
          <ToggleButton value='week' aria-label='Last 7 Days'>
            Last 7 Days
          </ToggleButton>
          <ToggleButton value='month' aria-label='Last 30 Days'>
            Last 30 Days
          </ToggleButton>
          <ToggleButton value='year' aria-label='Last Year'>
            Last Year
          </ToggleButton>
          <ToggleButton value='custom' aria-label='Custom'>
            Custom
            <ArrowDropDown
              sx={{
                ml: 0.25,
                transition: 'transform 0.2s',
                transform: open ? 'rotate(-180deg)' : 'rotate(0)'
              }}
            />
          </ToggleButton>
        </ToggleButtonGroup>
        <Box
          sx={{
            px: 1.5,
            py: 2,
            bgcolor: 'white',
            borderRadius: 1,
            typography: 'body1',
            color: 'text.secondary',
            whiteSpace: 'nowrap'
          }}
        >
          {fmt(startDate)} — {fmt(endDate)}
        </Box>
      </Box>
      {open && (
        <Box mt={1}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display='flex' alignItems='center' gap={1}>
              <DatePicker
                label='Start date'
                value={customStart}
                onChange={(v) => setCustomStart(v)}
                format='D MMM YYYY'
                slotProps={{ textField: { size: 'small', sx: { width: 160, bgcolor: 'white' } } }}
              />
              <span>to</span>
              <DatePicker
                label='End date'
                value={customEnd}
                onChange={(v) => setCustomEnd(v)}
                format='D MMM YYYY'
                slotProps={{ textField: { size: 'small', sx: { width: 160, bgcolor: 'white' } } }}
              />
              <Button variant='contained' size='small' disabled={invalid} onClick={handleApply}>
                Apply
              </Button>
            </Box>
          </LocalizationProvider>
        </Box>
      )}
    </div>
  )
}

export default TimeRangeFilter
